import React, { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import MUIDataTable from "mui-datatables";
import { ThemeProvider } from "@mui/material/styles";
import DATA_TABLE_THEME from "themes/DataTableTheme.js";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import Tabs from "components/utils/Tabs.js";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import DoneIcon from "@mui/icons-material/Done";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import { useParams } from "react-router";
import PatientService from "services/PatientService.js";
import UserService from "services/UserService.js";
import SaveIcon from "@mui/icons-material/Save";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import InputErrorMessage from "components/utils/InputErrorMessage.js";
import RegexUtils from "utils/RegexUtils.js";
import COUNTRY_CODES_LIST from "utils/constants/CountryCodesList.js";
import TableRowActionsCell from "components/utils/TableRowActionsCell.js";
import StringUtils from "utils/StringUtils.js";
import PopUpModal from "components/utils/PopUpModal.js";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import ZipcodeService from "services/ZipcodeService.js";
import ZipcodeUtils from "utils/ZipcodeUtils.js";
import useAPI from "hooks/useAPI.js";
import { useOutletContext, useLocation, useSearchParams } from "react-router-dom";
import PatientAlerts from "./PatientAlerts";
import moment from "moment";
import EnumUtils from "utils/EnumUtils.js";
import FormUtils from "utils/FormUtils.js";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

function PatientProfile() {
  const [hospitalId] = useOutletContext();
  const { patientId } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showAlertBanner, setShowAlertBanner] = useState(true);
  const tabsRef = useRef(null);
  const theme = useTheme();

  const tabItemNames = ["Basic Details", "Biometrics", "Forms", "Documents", "Alerts"];
  const tabViews = [
    <BasicDetails patientId={patientId} hospitalId={hospitalId} />,
    <Biometrics patientId={patientId} hospitalId={hospitalId} />,
    <Forms patientId={patientId} hospitalId={hospitalId} />,
    <Documents patientId={patientId} hospitalId={hospitalId} />,
    <PatientAlerts
      patientId={patientId}
      hospitalId={hospitalId}
      onView={() => markPatientNotificationsAsRead()}
    />,
  ];

  // Default tab: URL query ?tab=..., then location state (e.g. from notification), else first tab (never null so reload keeps tab)
  const tabFromUrl = searchParams.get("tab");
  const defaultTab =
    (tabFromUrl && tabItemNames.includes(tabFromUrl) ? tabFromUrl : null) ||
    location.state?.defaultTab ||
    tabItemNames[0];

  const handleTabChange = useCallback(
    (tabName) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", tabName);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Fetch unread notifications count for this patient
  const fetchUnreadNotificationCount = useCallback(async () => {
    try {
      const response = await UserService.getUserNotifications();
      const notifications = response.data || [];
      // Filter notifications for this patient that are unread
      // Handle both string and number comparison for patientId
      const unreadCount = notifications.filter(
        (n) => String(n.patientId) === String(patientId) && !n.isRead
      ).length;
      setUnreadNotificationCount(unreadCount);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    }
  }, [patientId]);

  useEffect(() => {
    fetchUnreadNotificationCount();
  }, [fetchUnreadNotificationCount]);

  async function markPatientNotificationsAsRead() {
    try {
      const response = await UserService.getUserNotifications();
      const notifications = response.data || [];
      // Find all unread notifications for this patient
      // Handle both string and number comparison for patientId
      const unreadNotifications = notifications.filter(
        (n) => String(n.patientId) === String(patientId) && !n.isRead
      );

      // Mark each as read
      for (const notification of unreadNotifications) {
        try {
          await UserService.markNotificationAsRead(notification.id);
        } catch (err) {
          console.error("Failed to mark notification as read", err);
        }
      }

      // Update the count
      setUnreadNotificationCount(0);
      // Refresh the count after marking as read
      await fetchUnreadNotificationCount();
    } catch (error) {
      console.error("Failed to mark notifications as read", error);
    }
  }

  // Prepare badge counts for tabs
  const tabBadges = {
    "Alerts": unreadNotificationCount > 0 ? unreadNotificationCount : undefined
  };

  // Debug: log badge count
  useEffect(() => {
    if (unreadNotificationCount > 0) {
      console.log("Unread notification count for patient:", unreadNotificationCount);
    }
  }, [unreadNotificationCount]);

  // Hide banner when notifications are marked as read
  useEffect(() => {
    if (unreadNotificationCount === 0) {
      setShowAlertBanner(false);
    } else {
      setShowAlertBanner(true);
    }
  }, [unreadNotificationCount]);

  const handleBannerClick = (e) => {
    // Only navigate if clicking on the alert itself, not on action buttons
    if (e.target.closest('.MuiAlert-action')) {
      return;
    }
    if (tabsRef.current) {
      tabsRef.current.changeTab("Alerts");
    }
  };

  const handleDismissBanner = (e) => {
    e.stopPropagation();
    setShowAlertBanner(false);
  };

  const handleViewAlertsClick = (e) => {
    e.stopPropagation();
    if (tabsRef.current) {
      tabsRef.current.changeTab("Alerts");
    }
  };

  return (
    <div key={patientId}>
      {showAlertBanner && unreadNotificationCount > 0 && (
        <Alert
          severity="info"
          icon={<NotificationsIcon />}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                onClick={handleViewAlertsClick}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                View Alerts
              </Button>
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleDismissBanner}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Box>
          }
          sx={{
            mb: 3,
            borderRadius: '8px',
            backgroundColor: theme.palette.info.light + '15',
            border: `1px solid ${theme.palette.info.main}40`,
            '& .MuiAlert-icon': {
              color: theme.palette.info.main,
            },
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            },
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.info.light + '25',
            }
          }}
          onClick={handleBannerClick}
        >
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              New Alert{unreadNotificationCount > 1 ? 's' : ''} Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You have {unreadNotificationCount} unread alert{unreadNotificationCount > 1 ? 's' : ''} for this patient. Click to view.
            </Typography>
          </Box>
        </Alert>
      )}
      <Tabs
        key={defaultTab}
        ref={tabsRef}
        itemNames={tabItemNames}
        views={tabViews}
        defaultTab={defaultTab}
        tabBadges={tabBadges}
        onTabChange={handleTabChange}
      />
    </div>
  );
}

function BasicDetails(props) {
  const [hospitals, setHospitals] = useState([]);
  const [patientParentList, setPatientParentList] = useState([]);
  const [isEditingEmail, setIsEditingEmail] = useState(false);

  // Fetching list of hospitals
  useAPI(UserService.getHospitals, getHospitalsHandler, false, true);

  const getPatientProfileAPI = useAPI(
    PatientService.getPatient,
    getPatientHandler
  );
  const updatePatientDetailsAPI = useAPI(
    PatientService.updatePatientDetails,
    true
  );
  const updatePatientEmailAPI = useAPI(
    PatientService.updatePatientEmail,
    () => { },
    true
  );
  const getPatientParentsAPI = useAPI(
    PatientService.getPatientParents,
    getPatientParentsHandler
  );
  const updateLocationAPI = useAPI(
    ZipcodeService.getLocationDetails,
    updateLocationHandler
  );

  const {
    register,
    formState: { errors },
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
  } = useForm({ criteriaMode: "all", mode: "onTouched" });

  const dobValue = watch("dob");
  const transferredByUserFullName = watch("transferredByUserFullName");

  const getPatientParents = useCallback(() => {
    getPatientParentsAPI.execute(props.hospitalId, props.patientId);
  }, [getPatientParentsAPI, props.hospitalId, props.patientId]);

  useEffect(() => {
    getPatientProfileAPI.execute(props.hospitalId, props.patientId);
    getPatientParents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.hospitalId, props.patientId]);

  /* Utility Function */

  function updateLocation(e) {
    let zipcode = e.target.value;
    updateLocationAPI.execute(zipcode);
  }

  function updatePatientDetails(data) {

    const payload = { ...data };

    if (!payload.addressLine1) payload.addressLine1 = null;
    if (!payload.addressLine2) payload.addressLine2 = null;
    if (!payload.zipcode) payload.zipcode = null;

    updatePatientDetailsAPI.execute(props.hospitalId, props.patientId, payload);
  }

  function updatePatientEmail() {
    updatePatientEmailAPI.execute(props.hospitalId, props.patientId, {
      email: getValues("email"),
    });
  }

  /* API Success Handlers */

  function getPatientHandler(response) {
    let data = response.data;
    if (data["maritalStatus"] == null) data["maritalStatus"] = "";

    reset(data);
  }

  function getHospitalsHandler(response) {
    let hospitalList = response.data;
    setHospitals(hospitalList);
  }

  function updateLocationHandler(response) {
    const { city, state, country } = ZipcodeUtils.getLocation(response.data);
    setValue("city", city);
    setValue("state", state);
    setValue("country", country);
  }

  function getPatientParentsHandler(response) {
    setPatientParentList(response.data);
  }

  // Show skeleton loader while patient data is loading
  if (getPatientProfileAPI.status === "pending" || !getPatientProfileAPI.response) {
    return (
      <Paper elevation={0} className="container py-4 px-4">
        <div className="row mb-4">
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-3 mb-3">
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-2 mb-3">
            <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-3 mb-3">
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-5 mb-3">
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-3 mb-3">
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="col col-4 mb-3">
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </div>
        </div>
        <Skeleton variant="rectangular" width={120} height={40} sx={{ mb: 3 }} />
        <Divider className="my-3">
          <Chip label="Parents" />
        </Divider>
        <div className="d-flex justify-content-end mb-3">
          <Skeleton variant="rectangular" width={130} height={36} />
        </div>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} className="container py-4 px-4">
      <form onSubmit={handleSubmit(updatePatientDetails)}>
        {transferredByUserFullName && (
          <div className="row mb-3">
            <div className="col col-12">
              <Typography variant="subtitle2" color="text.secondary">
                Transferred by: {transferredByUserFullName}
              </Typography>
            </div>
          </div>
        )}
        <div className="row mb-4">
          <div className="col col-4 mb-3">
            <label htmlFor="firstNameInput" className="form-label">
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              className="form-control"
              id="firstNameInput"
              {...register("firstName", {
                required: "First Name is required",
                pattern: {
                  value: RegexUtils.NAME,
                  message: "Fist Name is invalid",
                },
              })}
            />
            <InputErrorMessage name="firstName" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="lastNameInput" className="form-label">
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              className="form-control"
              id="lastNameInput"
              {...register("lastName", {
                required: "Last Name is required",
                pattern: {
                  value: RegexUtils.NAME,
                  message: "Last Name is invalid",
                },
              })}
            />
            <InputErrorMessage name="lastName" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="emailInput" className="form-label">
              Email ID
            </label>
            <OutlinedInput
              id="emailInput"
              name="email"
              type="text"
              size="small"
              sx={{ width: "100%" }}
              disabled={!isEditingEmail}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => {
                      if (isEditingEmail) {
                        updatePatientEmail();
                      }

                      setIsEditingEmail(!isEditingEmail);
                    }}
                    edge="end"
                    size="small"
                  >
                    {updatePatientEmailAPI.status === "pending" ? (
                      <CircularProgress size="20px" />
                    ) : isEditingEmail ? (
                      <DoneIcon fontSize="inherit" />
                    ) : (
                      <EditIcon fontSize="inherit" />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              {...register("email", {
                required: "Email ID is required",
                pattern: {
                  value: RegexUtils.EMAIL,
                  message: "Email ID is invalid",
                },
              })}
            />
          </div>
          <div className="col col-3 mb-3">
            <label htmlFor="dateOfBirthInput" className="form-label">
              Date of Birth
            </label>
            <input
              type="date"
              name="dob"
              className="form-control"
              id="dateOfBirthInput"
              {...register("dob", {
                required: "DOB is required",
              })}
            />
            <InputErrorMessage name="dob" errors={errors} />
          </div>
          <div className="col col-2 mb-3">
            <label htmlFor="ageInput" className="form-label">
              Age
            </label>
            <input
              type="number"
              className="form-control"
              id="ageInput"
              value={dobValue ? StringUtils.getAgeFromDOB(dobValue) : ""}
              disabled
            />
          </div>
          <div className="col col-3 mb-3">
            <label className="form-label d-block">Gender</label>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="gender"
                id="genderInputMale"
                value="Male"
                {...register("gender", {
                  required: "Gender is required",
                })}
              />
              <label className="form-check-label" htmlFor="genderInputMale">
                Male
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="gender"
                id="genderInputFemale"
                value="Female"
                {...register("gender", {
                  required: "Gender is required",
                })}
              />
              <label className="form-check-label" htmlFor="genderInputFemale">
                Female
              </label>
            </div>
            <InputErrorMessage name="gender" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="maritalStatusInput" className="form-label">
              Marital Status
            </label>
            <select
              className="form-select"
              name="maritalStatus"
              id="maritalStatusInput"
              aria-label="Default select example"
              {...register("maritalStatus", {
                required: "Marital Status is required",
              })}
            >
              <option></option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
              <option value="NA">NA</option>
            </select>
            <InputErrorMessage name="maritalStatus" errors={errors} />
          </div>
          <div className="col col-5 mb-3">
            <label className="form-label d-block">Age Group</label>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="ageGroup"
                id="ageGroupChildInput"
                value="Child"
                {...register("ageGroup")}
                disabled
              />
              <label className="form-check-label" htmlFor="ageGroupChildInput">
                Child
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="ageGroup"
                id="ageGroupAdolescentInput"
                value="Adolescent"
                {...register("ageGroup")}
                disabled
              />
              <label
                className="form-check-label"
                htmlFor="ageGroupAdolescentInput"
              >
                Adolescent
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="ageGroup"
                id="ageGroupAdultInput"
                value="Adult"
                {...register("ageGroup")}
                disabled
              />
              <label className="form-check-label" htmlFor="ageGroupAdultInput">
                Adult
              </label>
            </div>
          </div>
          <div className="col col-3 mb-3">
            <label htmlFor="phoneInput" className="form-label">
              Phone
            </label>
            <input
              type="text"
              name="phone"
              className="form-control"
              id="phoneInput"
              {...register("phone", {
                required: "Phone is required",
                pattern: {
                  value: RegexUtils.PHONE,
                  message: "Phone is invalid",
                },
              })}
            />
            <InputErrorMessage name="phone" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="primaryTherapistInput" className="form-label">
              Primary Therapist
            </label>
            <input
              type="text"
              name="primaryTherapist"
              className="form-control"
              id="primaryTherapistInput"
              {...register("primaryTherapist", {
                pattern: {
                  value: RegexUtils.NAME,
                  message: "Primary Therapist is invalid",
                },
              })}
            />
            <InputErrorMessage name="primaryTherapist" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="countryCode" className="form-label">
              Country Code
            </label>
            <select
              className="form-select"
              id="countryCodeInput"
              aria-label="Default select example"
              {...register("countryCode", {
                required: "Country Code is required",
                minLength: {
                  value: 2,
                  message: "Country Code must exceed 2 characters",
                },
                maxLength: {
                  value: 5,
                  message: "Country Code must not exceed 5 characters",
                },
              })}
            >
              <option></option>
              {COUNTRY_CODES_LIST.map((item, index) => (
                <option key={item.value + index} value={item.value}>
                  {item.country}
                </option>
              ))}
            </select>
            <InputErrorMessage name="countryCode" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="associatedHospitalIdInput" className="form-label">
              Associated Centre
            </label>
            <select
              className="form-select"
              name="associatedHospitalId"
              id="associatedHospitalIdInput"
              aria-label="Default select example"
              {...register("associatedHospitalId", {
                required: "Associated Centre is required",
              })}
            >
              <option></option>
              {hospitals.map((hospital) => (
                <option key={hospital.id} value={hospital.id}>
                  {hospital.hospitalName}
                </option>
              ))}
            </select>
            <InputErrorMessage name="associatedHospitalId" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="addressLine1Input" className="form-label">
              Address Line 1
            </label>
            <input
              type="text"
              className="form-control"
              id="addressLine1Input"
              {...register("addressLine1")}
            />
            <InputErrorMessage name="addressLine1" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="addressLine2Input" className="form-label">
              Address Line 2
            </label>
            <input
              type="text"
              className="form-control"
              id="addressLine2Input"
              {...register("addressLine2")}
            />
            <InputErrorMessage name="addressLine2" errors={errors} />
          </div>
          <div className="col col-4">
            <label htmlFor="zipcodeInput" className="form-label">
              Zipcode
            </label>
            <input
              type="text"
              className="form-control"
              id="zipcodeInput"
              {...register("zipcode", {
                validate: (value) => {
                  if (!value) return true; // optional
                  if (!RegexUtils.NUMERIC.test(value)) return "Zipcode should be numeric";
                  if (value.length < 4 || value.length > 6)
                    return "Zipcode must be 4 to 6 digits";
                  return true;
                },
              })}
              onBlur={(e) => {
                if (e.target.value) updateLocation(e);
              }}
            />
            <InputErrorMessage name="zipcode" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="cityInput" className="form-label">
              City
            </label>
            <input
              type="text"
              className="form-control"
              id="cityInput"
              {...register("city", {
                required: "City is required",
              })}
            />
            <InputErrorMessage name="city" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="stateInput" className="form-label">
              State
            </label>
            <input
              type="text"
              className="form-control"
              id="stateInput"
              {...register("state", {
                required: "State is required",
              })}
            />
            <InputErrorMessage name="state" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="countryInput" className="form-label">
              Country
            </label>
            <input
              type="text"
              name="country"
              className="form-control"
              id="countryInput"
              {...register("country", {
                required: "Country is required",
              })}
            />
            <InputErrorMessage name="country" errors={errors} />
          </div>
        </div>
        {updatePatientDetailsAPI.status === "pending" ? (
          <LoadingButton
            loading
            loadingPosition="start"
            startIcon={<SaveIcon />}
            variant="contained"
            className="me-3"
          >
            Update
          </LoadingButton>
        ) : (
          <Button
            type="submit"
            className="me-3"
            startIcon={<DoneIcon />}
            variant="contained"
          >
            Update
          </Button>
        )}
      </form>
      <Divider className="my-3">
        <Chip label="Parents" />
      </Divider>
      <div>
        <div className="d-flex justify-content-end">
          <Button
            size="small"
            onClick={() => {
              setPatientParentList((prevState) => [...prevState, {}]);
            }}
            startIcon={<AddIcon color="primary" />}
            variant="outlined"
          >
            Add Parent
          </Button>
        </div>
        <div className="mt-3 d-flex flex-wrap justify-content-between">
          {patientParentList.map((parent, index) => {
            return (
              <ParentCard
                key={`parent-card-${index}`}
                index={index}
                isAddingParent={Object.keys(parent).length === 0}
                hospitalId={props.hospitalId}
                patientId={props.patientId}
                parentData={parent}
                refreshParentsListHandler={() => {
                  setPatientParentList([]);
                  getPatientParents();
                }}
              />
            );
          })}
        </div>
      </div>
    </Paper>
  );
}

function Biometrics(props) {
  const theme = useTheme();

  const getPatientBiometricsAPI = useAPI(
    PatientService.getPatientBiometrics,
    getPatientBiometricsHandler
  );
  const updatePatientBiometricsAPI = useAPI(
    PatientService.updatePatientBiometrics,
    () => { },
    true
  );

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({ criteriaMode: "all", mode: "onTouched" });

  useEffect(() => {
    getPatientBiometricsAPI.execute(props.hospitalId, props.patientId);
  }, []);

  function getPatientBiometricsHandler(response) {
    let data = response.data;
    for (let key in data) {
      if (data[key] == null) data[key] = "";
    }
    reset(data);
  }

  function updatePatientBiometrics(data) {
    updatePatientBiometricsAPI.execute(props.hospitalId, props.patientId, data);
  }

  return (
    <Paper elevation={0}>
      <form
        className="container py-4 px-4"
        onSubmit={handleSubmit(updatePatientBiometrics)}
      >
        <div className="row mb-4">
          <div className="col col-4 mb-3">
            <label htmlFor="heightInput" className="form-label">
              Height{" "}
              <span>
                <Typography variant="caption" color={theme.palette.text.main}>
                  (in Cms)
                </Typography>
              </span>
            </label>
            <input
              type="text"
              name="height"
              className="form-control"
              id="heightInput"
              {...register("height", {
                required: "Height is required",
                pattern: {
                  value: RegexUtils.NUMERIC,
                  message: "Height is invalid",
                },
              })}
            />
            <InputErrorMessage name="height" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="weightInput" className="form-label">
              Weight{" "}
              <span>
                <Typography variant="caption" color={theme.palette.text.main}>
                  (in Lbs)
                </Typography>
              </span>
            </label>
            <input
              type="text"
              name="weight"
              className="form-control"
              id="weightInput"
              {...register("weight", {
                required: "Weight is required",
                pattern: {
                  value: RegexUtils.NUMERIC,
                  message: "Weight is invalid",
                },
              })}
            />
            <InputErrorMessage name="weight" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="bmiInput" className="form-label">
              BMI
            </label>
            <input
              type="text"
              name="bmi"
              className="form-control"
              id="bmiInput"
              {...register("bmi", {
                required: "BMI is required",
                pattern: {
                  value: RegexUtils.NUMERIC,
                  message: "BMI is invalid",
                },
              })}
            />
            <InputErrorMessage name="bmi" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label className="form-label d-block">Deficit</label>
            <div className="form-check form-check-inline mt-2">
              <input
                className="form-check-input"
                type="radio"
                name="deficit"
                id="deficitInputLeft"
                value="Left"
                {...register("deficit", {
                  required: "Deficit is required",
                })}
              />
              <label className="form-check-label" htmlFor="deficitInputLeft">
                Left
              </label>
            </div>
            <div className="form-check form-check-inline mt-2">
              <input
                className="form-check-input"
                type="radio"
                name="deficit"
                id="deficitInputRight"
                value="Right"
                {...register("deficit", {
                  required: "Deficit is required",
                })}
              />
              <label className="form-check-label" htmlFor="deficitInputRight">
                Right
              </label>
            </div>
            <div className="form-check form-check-inline mt-2">
              <input
                className="form-check-input"
                type="radio"
                name="deficit"
                id="deficitInputNotApplicable"
                value="NA"
                {...register("deficit", {
                  required: "Deficit is required",
                })}
              />
              <label
                className="form-check-label"
                htmlFor="deficitInputNotApplicable"
              >
                NA
              </label>
            </div>
            <InputErrorMessage name="deficit" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="hairColorInput" className="form-label">
              Hair Color
            </label>
            <input
              type="text"
              name="hairColor"
              className="form-control"
              id="hairColorInput"
              {...register("hairColor", {
                required: "Hair Color is required",
              })}
            />
            <InputErrorMessage name="hairColor" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="eyeColorInput" className="form-label">
              Eye Color
            </label>
            <input
              type="text"
              name="eyeColor"
              className="form-control"
              id="eyeColorInput"
              {...register("eyeColor", {
                required: "Eye Color is required",
              })}
            />
            <InputErrorMessage name="eyeColor" errors={errors} />
          </div>
          <div className="col col-4 mb-3">
            <label htmlFor="bloodGroupInput" className="form-label">
              Blood Group
            </label>
            <select
              className="form-select"
              name="bloodGroup"
              id="bloodGroupInput"
              aria-label="Default select example"
              {...register("bloodGroup", {
                required: "Blood Group is required",
              })}
            >
              <option>Select Group</option>
              <option value="A+">A+</option>
              <option value="A+">A-</option>
              <option value="B-">B+</option>
              <option value="B+">B-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
            </select>
          </div>
          <InputErrorMessage name="bloodGroup" errors={errors} />
        </div>
        {updatePatientBiometricsAPI.status === "pending" ? (
          <LoadingButton
            loading
            loadingPosition="start"
            startIcon={<SaveIcon />}
            variant="contained"
            className="me-3"
          >
            Update
          </LoadingButton>
        ) : (
          <Button
            type="submit"
            className="me-3"
            startIcon={<DoneIcon />}
            variant="contained"
          >
            Update
          </Button>
        )}
      </form>
    </Paper>
  );
}

function Forms(props) {
  const [intakePacks, setIntakePacks] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [formsData, setFormsData] = useState({});
  const currentIntakeIdRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [intakeName, setIntakeName] = useState('');
  const [description, setDescription] = useState('');
  const [patientAgeGroup, setPatientAgeGroup] = useState(null);
  const [selectedBundleType, setSelectedBundleType] = useState("");
  const [formCreationMode, setFormCreationMode] = useState("BUNDLE"); // "BUNDLE" or "CONSENT"
  const [selectedConsentType, setSelectedConsentType] = useState("PATIENT_CONSENT");

  // Intake bundle options; value must match server IntakeBundleTypeEnum
  const INTAKE_BUNDLES = [
    {
      value: "ADULT_FULL_INTAKE_NEW_ASSESSMENT",
      label: "Full Intake - Adult New Assessment",
      formLabels: [
        "Adult Patient Consent Form",
        "Adult Demographic Info Checklist",
        "Adult History",
        "Adult Hemispheric Checklist",
        "Adult Cognitive Style Assessment",
        "Adult Sensory Function Checklist",
      ],
    },
    {
      value: "CHILD_FULL_INTAKE_NEW_ASSESSMENT",
      label: "Full Intake - Child New Assessment",
      formLabels: [
        "Child Patient Consent Form",
        "Child Demographic Info Checklist",
        "Child History",
        "Child Hemispheric Checklist",
        "Mother Hemispheric Checklist",
        "Father Hemispheric Checklist",
      ],
    },
    {
      value: "ADULT_REASSESSMENT",
      label: "Follow Up - Adult Reassessment",
      formLabels: [
        "Adult Follow Up Form",
        "Patient Consent Form",
        "Adult Hemispheric Checklist",
      ],
    },
    {
      value: "CHILD_REASSESSMENT",
      label: "Follow Up - Child Reassessment",
      formLabels: [
        "Child Follow Up Form",
        "Patient Consent Form",
        "Child Hemispheric Checklist",
      ],
    },
    {
      value: "ADULT_NEURODEVELOPMENTAL_NEW_ASSESSMENT",
      label: "Neurodevelopmental Services - Adult New Assessment",
      formLabels: [
        "Neurodevelopmental Services Consent Form",
        "Adult History",
        "Adult Hemispheric Checklist",
        "Adult Cognitive Style Assessment",
        "Adult Sensory Function Checklist",
      ],
    },
    {
      value: "ADULT_NEURODEVELOPMENTAL_REASSESSMENT",
      label: "Neurodevelopmental Services - Adult Reassessment",
      formLabels: [
        "Neurodevelopmental Services Consent Form",
        "Adult Hemispheric Checklist",
      ],
    },
    {
      value: "CHILD_NEURODEVELOPMENTAL_NEW_ASSESSMENT",
      label: "Neurodevelopmental Services - Child New Assessment",
      formLabels: [
        "Neurodevelopmental Services Consent Form",
        "Child History",
        "Child Hemispheric Checklist",
      ],
    },
    {
      value: "CHILD_NEURODEVELOPMENTAL_REASSESSMENT",
      label: "Neurodevelopmental Services - Child Reassessment",
      formLabels: [
        "Neurodevelopmental Services Consent Form",
        "Child Hemispheric Checklist",
      ],
    },
  ];

  // Adult/Adolescent → adult bundles only; Child → child bundles only
  function getBundlesForAgeGroup(ageGroup) {
    if (!ageGroup) return [];
    const isAdultOrAdolescent = ageGroup === "Adult" || ageGroup === "Adolescent";
    return INTAKE_BUNDLES.filter((b) =>
      isAdultOrAdolescent ? b.value.startsWith("ADULT_") : b.value.startsWith("CHILD_")
    );
  }

  // Helper function to format dates using moment
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString, "YYYY-MM-DD hh:mm a")
      .utc(dateString)
      .local()
      .format("MM/DD/YYYY hh:mm A");
  };

  const getIntakePacksAPI = useAPI(
    PatientService.getIntakePacks,
    getIntakePacksHandler
  );

  const getIntakeFormsAPI = useAPI(
    PatientService.getIntakeForms,
    getIntakeFormsHandler,
    false,
    false
  );

  const createIntakePackAPI = useAPI(
    PatientService.createIntakePack,
    createIntakePackHandler,
    true
  );

  const getPatientAPI = useAPI(
    PatientService.getPatient,
    getPatientHandler
  );

  useEffect(() => {
    getIntakePacksAPI.execute(props.hospitalId, props.patientId);
    getPatientAPI.execute(props.hospitalId, props.patientId);
  }, []);

  function getPatientHandler(response) {
    const ageGroup = response.data?.ageGroup;
    setPatientAgeGroup(ageGroup);

    // Keep bundle selection only if it's valid for this age group; otherwise pre-select first bundle
    if (ageGroup) {
      const bundles = getBundlesForAgeGroup(ageGroup);
      const validValues = bundles.map((b) => b.value);
      setSelectedBundleType((prev) =>
        validValues.includes(prev) ? prev : (bundles.length > 0 ? bundles[0].value : "")
      );
    } else {
      setSelectedBundleType("");
    }
  }

  function getIntakePacksHandler(response) {
    setIntakePacks(response.data || []);
  }

  function getIntakeFormsHandler(response) {
    if (currentIntakeIdRef.current) {
      setFormsData(prev => ({
        ...prev,
        [currentIntakeIdRef.current]: response.data || []
      }));
    }
  }

  function createIntakePackHandler(response) {
    // Refresh the intake packs list after successful creation
    getIntakePacksAPI.execute(props.hospitalId, props.patientId);
    setIsModalOpen(false);
    setIntakeName('');
    setDescription('');
    // Reset bundle selection to age-based default for next time
    if (patientAgeGroup) {
      const bundles = getBundlesForAgeGroup(patientAgeGroup);
      setSelectedBundleType(bundles.length > 0 ? bundles[0].value : "");
    } else {
      setSelectedBundleType("");
    }
  }

  const handleRowClick = (intakeId) => {
    setExpandedRows(prev => ({
      ...prev,
      [intakeId]: !prev[intakeId]
    }));

    // Fetch forms for this intake pack if not already loaded
    if (!formsData[intakeId]) {
      currentIntakeIdRef.current = intakeId;
      getIntakeFormsAPI.execute(props.hospitalId, props.patientId, intakeId);
    }
  };

  const updateFormStatus = (intakeId, formId, newStatus) => {
    setFormsData(prev => {
      const intakeForms = prev[intakeId] || [];
      return {
        ...prev,
        [intakeId]: intakeForms.map(form =>
          form.id === formId
            ? { ...form, formStatusTypeEnum: newStatus }
            : form
        )
      };
    });
  };

  const formatTypeLabel = (type) => {
    if (!type) return '';
    return type
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const applyDefaultsForMode = (mode) => {
    if (mode === "BUNDLE") {
      const bundles = getBundlesForAgeGroup(patientAgeGroup);
      if (bundles.length > 0) {
        const firstBundle = bundles[0];
        setSelectedBundleType(firstBundle.value);
        setIntakeName(firstBundle.label);
      } else {
        setSelectedBundleType("");
        setIntakeName("");
      }
    } else if (mode === "CONSENT") {
      const defaultConsent = "PATIENT_CONSENT";
      setSelectedConsentType(defaultConsent);
      setIntakeName(formatTypeLabel(defaultConsent));
    }
  };

  function handleBundleTypeChange(e) {
    const bundleType = e.target.value;
    setSelectedBundleType(bundleType);
    setIntakeName(formatTypeLabel(bundleType));
  }

  function handleConsentTypeChange(e) {
    const consentType = e.target.value;
    setSelectedConsentType(consentType);
    setIntakeName(formatTypeLabel(consentType));
  }

  return (
    <Paper elevation={0} className="p-3">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Intake Packs
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setIsModalOpen(true);
            applyDefaultsForMode(formCreationMode);
          }}
        >
          Send New Intake Bundle
        </Button>
      </Box>
      {
        getIntakePacksAPI.status === "pending" ? (
          <div className="text-center">
            <CircularProgress
              className="text-center"
              sx={{
                my: 20,
              }}
            />
          </div>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Intake</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {intakePacks.map((pack) => (
                  <React.Fragment key={pack.id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => handleRowClick(pack.id)}
                        >
                          {expandedRows[pack.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{pack.packageName || 'N/A'}</TableCell>
                      <TableCell>{pack.description || 'N/A'}</TableCell>
                      <TableCell>{formatDate(pack.createdAt)}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleRowClick(pack.id)}
                          startIcon={expandedRows[pack.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        >
                          {expandedRows[pack.id] ? 'Hide Forms' : 'Show Forms'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={expandedRows[pack.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            {getIntakeFormsAPI.status === "pending" ? (
                              <CircularProgress size={24} />
                            ) : (
                              <Table size="small" aria-label="forms">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Form Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Updated At</TableCell>
                                    <TableCell>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(formsData[pack.id] || []).map((form) => (
                                    <TableRow key={form.id}>
                                      <TableCell>{EnumUtils.parse(form.registrationFormTypeEnum)}</TableCell>
                                      <TableCell>{FormUtils.getStatusChip(form.formStatusTypeEnum)}</TableCell>
                                      <TableCell>{formatDate(form.lastUpdatedAt)}</TableCell>
                                      <TableCell>
                                        <FormActions
                                          form={form}
                                          hospitalId={props.hospitalId}
                                          patientId={props.patientId}
                                          intakeId={pack.id}
                                          onFormStatusUpdate={updateFormStatus}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      }

      {/* Send New Intake Bundle Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send New Intake Bundle</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Creating a new intake pack will send an email to the patient to notify them to fill out the forms in the pack.
          </Alert>

          <TextField
            margin="dense"
            label="Intake Name"
            fullWidth
            variant="outlined"
            value={intakeName}
            onChange={(e) => setIntakeName(e.target.value)}
            InputLabelProps={{ shrink: Boolean(intakeName) }}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Enter a description to send in the email to the patient.
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              What would you like to send?
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={formCreationMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  setFormCreationMode(mode);
                  applyDefaultsForMode(mode);
                }}
              >
                <FormControlLabel
                  value="BUNDLE"
                  control={<Radio size="small" />}
                  label="Intake bundle"
                />
                <FormControlLabel
                  value="CONSENT"
                  control={<Radio size="small" />}
                  label="Consents"
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {formCreationMode === "BUNDLE" && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Select Bundle:
              </Typography>
              <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                <InputLabel id="intake-bundle-label">Bundle</InputLabel>
                <Select
                  labelId="intake-bundle-label"
                  value={selectedBundleType}
                  onChange={handleBundleTypeChange}
                  label="Bundle"
                >
                  <MenuItem value="">
                    <em>Choose a bundle</em>
                  </MenuItem>
                  {getBundlesForAgeGroup(patientAgeGroup).map((bundle) => (
                    <MenuItem key={bundle.value} value={bundle.value}>
                      {bundle.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedBundleType && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                    Forms in this bundle:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, pt: 0.5 }}>
                    {(INTAKE_BUNDLES.find((b) => b.value === selectedBundleType)?.formLabels || []).map((label) => (
                      <li key={label}>
                        <Typography variant="body2" component="span">{label}</Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {formCreationMode === "CONSENT" && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Consents:
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={selectedConsentType}
                  onChange={handleConsentTypeChange}
                >
                  <FormControlLabel
                    value="PATIENT_CONSENT"
                    control={<Radio size="small" />}
                    label="Patient Consent Form"
                  />
                  <FormControlLabel
                    value="LENS_NFB_CONSENT"
                    control={<Radio size="small" />}
                    label="LENS Neurofeedback Consent Form"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          )}

          {!patientAgeGroup && getPatientAPI.status === "pending" && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const payload = {
                packageName: intakeName,
                description: description,
              };

              if (formCreationMode === "BUNDLE") {
                payload.intakeBundleType = selectedBundleType;
              } else if (formCreationMode === "CONSENT" && selectedConsentType) {
                payload.selectedFormTypes = [selectedConsentType];
              }

              createIntakePackAPI.execute(props.hospitalId, props.patientId, payload);
            }}
            variant="contained"
            color="primary"
            disabled={
              createIntakePackAPI.loading ||
              (formCreationMode === "BUNDLE" ? !selectedBundleType : !selectedConsentType)
            }
          >
            {createIntakePackAPI.loading ? 'Creating...' : 'Send Intake Pack'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}


function FormActions({ form, hospitalId, patientId, intakeId, onFormStatusUpdate }) {
  const revokeFormSubmissionAPI = useAPI(
    PatientService.revokeIntakeFormSubmission,
    revokeFormSubmissionHandler,
    true
  );
  const markFormCompletedAPI = useAPI(
    PatientService.markIntakeFormCompleted,
    markFormCompletedHandler,
    true
  );
  const sendReminderEmailAPI = useAPI(
    PatientService.sendReminderEmail,
    sendReminderEmailHandler,
    true
  );

  const actions = {
    startItems: [
      {
        component: <VisibilityIcon fontSize="inherit" color="secondary" />,
        onClickHandler: () =>
          navigateToForm(
            form.registrationFormTypeEnum,
            hospitalId,
            patientId,
            intakeId,
            form.id
          ),
      },
    ],
    menuItems: [],
  };

  // Add "revoke submission" option for forms that are not NOT_STARTED
  if (form.formStatusTypeEnum === "SUBMITTED") {
    actions.menuItems.push({
      label: "Revoke Submission",
      onClickHandler: () =>
        revokeFormSubmission(hospitalId, patientId, intakeId, form.id),
    });
  }

  // Add "revoke submission" option for forms that are not NOT_STARTED
  if (form.formStatusTypeEnum !== "SUBMITTED") {
    actions.menuItems.push({
      label: "Mark Completed",
      onClickHandler: () =>
        markFormCompleted(hospitalId, patientId, intakeId, form.id),
    });
  }

  // Add "Send Reminder Email" option for all forms
  if (form.formStatusTypeEnum !== "SUBMITTED") {
    actions.menuItems.push({
      label:
        sendReminderEmailAPI.status === "pending"
          ? "Sending Email..."
          : "Send Reminder Email",
      onClickHandler: () =>
        sendReminderEmail(hospitalId, patientId, intakeId, form.id),
      disabled: sendReminderEmailAPI.status === "pending",
    });
  }


  function revokeFormSubmission(hospitalId, patientId, intakeId, formId) {
    revokeFormSubmissionAPI.execute(hospitalId, patientId, intakeId, formId);

  }

  function markFormCompleted(hospitalId, patientId, intakeId, formId) {
    markFormCompletedAPI.execute(hospitalId, patientId, intakeId, formId);

  }

  function sendReminderEmail(hospitalId, patientId, intakeId, formId) {
    if (
      window.confirm(
        "Are you sure you want to send a reminder email for this form?"
      )
    ) {
      sendReminderEmailAPI.execute(hospitalId, patientId, intakeId, formId);
    }
  }

  function revokeFormSubmissionHandler(response) {
    if (onFormStatusUpdate) {
      onFormStatusUpdate(intakeId, form.id, "INCOMPLETE");
    }
  }

  function markFormCompletedHandler(response) {
    if (onFormStatusUpdate) {
      onFormStatusUpdate(intakeId, form.id, "SUBMITTED");
    }
  }

  function sendReminderEmailHandler(response) {
    alert("Reminder email sent successfully!");
  }

  function navigateToForm(
    patientFormTypeEnum,
    hospitalId,
    patientId,
    intakeId,
    formId
  ) {
    if (patientFormTypeEnum === "DEMOGRAPHIC_INFO_CHECKLIST_CHILD") {
      window.open(
        `user-forms/patient-registration/child-demographic-info-checklist.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "HISTORY_CHILD") {
      window.open(
        `user-forms/patient-registration/child-history.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "HEMISPHERIC_CHECKLIST_CHILD") {
      window.open(
        `user-forms/patient-registration/child-hemispheric-checklist.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "DEMOGRAPHIC_INFO_CHECKLIST_ADULT") {
      window.open(
        `user-forms/patient-registration/adult-demographic-info-checklist.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "HISTORY_ADULT") {
      window.open(
        `user-forms/patient-registration/adult-history.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "HEMISPHERIC_CHECKLIST_ADULT") {
      window.open(
        `user-forms/patient-registration/adult-hemispheric-checklist.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "COGNITIVE_STYLE_ASSESSMENT_ADULT") {
      window.open(
        `user-forms/patient-registration/adult-cognitive-style-assessment.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "SENSORY_FUNCTION_CHECKLIST_ADULT") {
      window.open(
        `user-forms/patient-registration/adult-sensory-function-checklist.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "PATIENT_CONSENT") {
      window.open(
        `user-forms/patient-registration/patient-consent.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "HISTORY_CHILD_V2") {
      window.open(
        `user-forms/patient-registration/child-history-v2.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "HISTORY_ADULT_V2") {
      window.open(
        `user-forms/patient-registration/adult-history-v2.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "ADULT_FOLLOW_UP") {
      window.open(
        `user-forms/patient-registration/adult-follow-up.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "CHILD_FOLLOW_UP") {
      window.open(
        `user-forms/patient-registration/child-follow-up.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "LENS_NFB_CONSENT") {
      window.open(
        `user-forms/patient-registration/lens-consent.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "NEURODEVELOPMENTAL_SERVICES_CONSENT") {
      window.open(
        `user-forms/patient-registration/neurodevelopmental-consent.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "MOTHER_HEMISPHERIC_CHECKLIST") {
      window.open(
        `user-forms/patient-registration/mother-hemispheric-checklist.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else if (patientFormTypeEnum === "FATHER_HEMISPHERIC_CHECKLIST") {
      window.open(
        `user-forms/patient-registration/father-hemispheric-checklist.html?hospitalID=${hospitalId}&patientID=${patientId}&intakeID=${intakeId}&registrationFormID=${formId}&viewMode=read`,
        "_blank"
      );
    } else {
      alert("Invalid Patient Assessment Form Type");
    }
  }

  return <TableRowActionsCell actions={actions} />;
}


function Documents(props) {

  const DOCUMENT_FOLDERS = [
    {
      key: "PATIENTS",
      label: "Patients",
      children: [
        { key: "PATIENT_UPLOADS", label: "Uploads" },
        { key: "PATIENT_DOCUMENTS", label: "Documents" },
      ],
    },
    {
      key: "FUNCTIONAL_TESTING",
      label: "Functional Testing",
      children: [
        { key: "AUDITORY", label: "Auditory" },
        { key: "VISUAL", label: "Visual" },
        { key: "IM", label: "IM" },
      ],
    },
    { key: "LAB_TESTS", label: "Lab Tests" },
    { key: "ACADEMICS", label: "Academics/WIAT Testing" },
    { key: "HOME_PROGRAM", label: "Home Program" },
    { key: "QEEG", label: "QEEG/Neurofeedback" },
  ];

  /** For docs with no folder or legacy PATIENT_UPLOADS, categorize by uploadedByPatient; else use folderKey. */
  function getDisplayFolderKey(doc) {
    const key = doc.folderKey == null || doc.folderKey === "" ? null : doc.folderKey;
    if (key === null) {
      return doc.uploadedByPatient ? "PATIENT_UPLOADS" : "PATIENT_DOCUMENTS";
    }
    return key;
  }

  const [originalDocumentsList, setOriginalDocumentsList] = useState([]);
  const [documentsTableData, setDocumentsTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFolderKey, setSelectedFolderKey] = useState("PATIENT_UPLOADS");

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadError, setUploadError] = useState("");

  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const documentsDisplayDataRef = useRef([]);
  const selectedDocumentIdsRef = useRef([]);

  const [renamedFilesMap, setRenamedFilesMap] = useState({});
  const [showRenamePrompt, setShowRenamePrompt] = useState(false);

  const inputFileRef = useRef(null);
  const popUpModalRef = useRef(null);
  const [popUpModalContent, setPopUpModalContent] = useState({
    documentId: "",
  });
  const getDocumentsAPI = useAPI(
    PatientService.getPatientDocuments,
    getDocumentsHandler
  );
  const uploadDocumentAPI = useAPI(
    PatientService.uploadPatientDocument,
    uploadDocumentHandler,
    true
  );
  const deleteDocumentAPI = useAPI(
    PatientService.deletePatientDocument,
    () => getDocuments(),
    true
  );
  const deleteDocumentsAPI = useAPI(
    PatientService.deletePatientDocuments,
    () => getDocuments(),
    true
  );
  const moveDocumentAPI = useAPI(
    PatientService.updatePatientDocumentFolder,
    () => getDocuments(),
    true
  );
  const renameDocumentAPI = useAPI(
    PatientService.renamePatientDocument,
    () => getDocuments(),
    true
  );
  const getDocuments = useCallback(() => {
    getDocumentsAPI.execute(props.hospitalId, props.patientId);
  }, []);

  useEffect(() => {
    getDocuments();
  }, []);

  function formatDocumentDate(dateString) {
    if (!dateString) return "N/A";
    return moment(dateString).format("MM/DD/YYYY hh:mm A");
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      let result = originalDocumentsList;

      if (selectedFolderKey) {
        result = result.filter((doc) => getDisplayFolderKey(doc) === selectedFolderKey);
      }

      if (searchTerm.trim()) {
        const lower = searchTerm.toLowerCase();
        result = result.filter((doc) =>
          doc.originalFilename.toLowerCase().includes(lower)
        );
      }

      const tableRows = result.map((doc) => ({
        id: doc.id,
        fileName: doc.originalFilename || "",
        updatedAt: formatDocumentDate(doc.lastUpdatedAt),
        createdAt: formatDocumentDate(doc.createdAt ?? doc.created_at),
        folderKey: getDisplayFolderKey(doc),
        s3FileUrl: doc.s3FileUrl,
        originalFilename: doc.originalFilename,
      }));
      setDocumentsTableData(tableRows);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm, originalDocumentsList, selectedFolderKey]);

  useEffect(() => {
    // Clear selection when folder or search changes so bulk actions
    // apply only to currently visible documents.
    setSelectedDocumentIds([]);
  }, [selectedFolderKey, searchTerm]);

  function uploadDocumentHandler() {
    setSelectedFiles([]);
    setRenamedFilesMap({});
    setShowRenamePrompt(false);
    setUploadError("");
    getDocuments();
  }

  function deleteDocument(documentId) {
    deleteDocumentAPI.execute(props.hospitalId, props.patientId, documentId);
  }

  function deleteDocuments(documentIds) {
    if (!Array.isArray(documentIds) || documentIds.length === 0) return;
    if (documentIds.length === 1) {
      deleteDocumentAPI.execute(props.hospitalId, props.patientId, documentIds[0]);
    } else {
      deleteDocumentsAPI.execute(props.hospitalId, props.patientId, documentIds);
    }
    setSelectedDocumentIds([]);
  }

  function moveDocuments(documentIds, targetFolderKey) {
    if (!Array.isArray(documentIds) || documentIds.length === 0 || !targetFolderKey) return;
    documentIds.forEach((id) => {
      moveDocumentAPI.execute(props.hospitalId, props.patientId, id, targetFolderKey);
    });
    setSelectedDocumentIds([]);
  }

  function getValidUploadFolders() {
    const uploadFolders = [];
    for (const folder of DOCUMENT_FOLDERS) {
      if (folder.children && folder.children.length > 0) {
        uploadFolders.push(...folder.children);
      } else {
        uploadFolders.push(folder);
      }
    }
    return uploadFolders;
  }

  function getSelectedFolderLabel(key) {
    if (!key) return null;
    for (const folder of DOCUMENT_FOLDERS) {
      if (folder.children) {
        const child = folder.children.find((c) => c.key === key);
        if (child) return `${folder.label} > ${child.label}`;
      }
      if (folder.key === key) return folder.label;
    }
    return key;
  }

  function isValidFileType(file) {
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png", ".docx"];
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
    return allowedTypes.includes(file.type) || allowedExtensions.includes(extension);
  }

  function isValidFileSize(file) {
    const maxSize = 10 * 1024 * 1024;
    return file.size <= maxSize;
  }

  function validateFiles(filesToValidate) {
    if (!filesToValidate || filesToValidate.length === 0) {
      setUploadError("Please select at least one file.");
      return false;
    }

    for (let i = 0; i < filesToValidate.length; i++) {
      const file = filesToValidate[i];
      if (!isValidFileType(file)) {
        setUploadError(`Invalid file type: ${file.name}. Allowed types: PDF, JPG, JPEG, PNG, DOCX.`);
        return false;
      }
      if (!isValidFileSize(file)) {
        setUploadError(`File size exceeds 10 MB: ${file.name}`);
        return false;
      }
    }

    return true;
  }

  function uploadDocument(filesToUpload) {
    if (!selectedFolderKey) {
      setUploadError("Please select a document folder from the folder tree on the left before uploading.");
      return;
    }

    if (!validateFiles(filesToUpload)) {
      return;
    }

    const renamedFilenames = [];
    for (let i = 0; i < filesToUpload.length; i++) {
      const renamedName = renamedFilesMap[i];

      if (!renamedName || renamedName.trim() === "") {
        setUploadError(`File ${i + 1} (${filesToUpload[i].name}) has not been renamed. All files must be renamed before upload.`);
        return;
      }

      renamedFilenames.push(renamedName.trim());
    }

    setUploadError("");

    const formData = new FormData();
    formData.append("folderKey", selectedFolderKey);

    for (let i = 0; i < renamedFilenames.length; i++) {
      formData.append("renamedFilenames", renamedFilenames[i]);
    }

    for (let i = 0; i < filesToUpload.length; i++) {
      formData.append("files", filesToUpload[i]);
    }

    uploadDocumentAPI.execute(props.hospitalId, props.patientId, formData);
  }

  function getDocumentsHandler(response) {
    setOriginalDocumentsList(response.data);
    setSelectedDocumentIds([]);
  }

  function handleFileInputChange(event) {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFiles(Array.from(files));
      setRenamedFilesMap({});
      setShowRenamePrompt(true);
      setUploadError("");
    }

    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  }

  const onFileChange = (e) => {
    handleFileInputChange(e);
  };
  const onUploadButtonClick = () => {
    /* Collecting node-element and performing click */
    inputFileRef.current.click();
  };

  function openModal() {
    popUpModalRef.current.openModal();
  }

  function closeModal() {
    popUpModalRef.current.closeModal();
  }

  const folderDocumentCounts = React.useMemo(() => {
    const counts = {};
    originalDocumentsList.forEach((doc) => {
      const key = getDisplayFolderKey(doc);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [originalDocumentsList]);

  function getFolderCount(folder) {
    if (folder.children && folder.children.length > 0) {
      return folder.children.reduce((sum, child) => sum + (folderDocumentCounts[child.key] || 0), 0);
    }
    return folderDocumentCounts[folder.key] || 0;
  }

  const documentsColumns = [
    { name: "id", label: "ID", options: { display: "excluded", filter: false, sort: false } },
    { name: "fileName", label: "File Name", options: { sort: true, filter: false } },
    { name: "updatedAt", label: "Updated At", options: { sort: true, filter: false } },
    { name: "createdAt", label: "Created At", options: { sort: true, filter: false } },
    { name: "s3FileUrl", label: "Link", options: { display: "excluded", filter: false, sort: false } },
    { name: "folderKey", label: "Folder", options: { display: "excluded", filter: false, sort: false } },
    { name: "originalFilename", label: "Original", options: { display: "excluded", filter: false, sort: false } },
    {
      name: "actions",
      label: "Actions",
      options: {
        sort: false,
        filter: false,
        download: false,
        customBodyRender: (value, tableMeta) => {
          const rowData = tableMeta.rowData;
          if (!Array.isArray(rowData) || rowData.length < 6) return null;
          const doc = {
            id: rowData[0],
            s3FileUrl: rowData[4],
            folderKey: rowData[5],
            originalFilename: rowData[6] ?? rowData[1],
          };
          if (!doc.id) return null;
          return (
            <Box component="span" data-actions-cell onClick={(e) => e.stopPropagation()} sx={{ display: "inline-flex" }}>
              <ActionsCell
                documentId={doc.id}
                link={doc.s3FileUrl}
                currentFolderKey={doc.folderKey}
                currentFileName={doc.originalFilename}
              />
            </Box>
          );
        },
      },
    },
  ];

  const documentsTableOptions = {
    filter: false,
    search: false,
    print: false,
    download: false,
    viewColumns: false,
    responsive: "standard",
    selectableRows: "multiple",
    selectableRowsHeader: true,
    sortOrder: { name: "updatedAt", direction: "desc" },
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 25],
    customToolbar: ({ displayData }) => {
      documentsDisplayDataRef.current = displayData || [];
      return null;
    },
    onRowSelectionChange: (currentRowsSelected, allRowsSelected, rowsSelected) => {
      if (Array.isArray(rowsSelected) && rowsSelected.length > 0) {
        const ids = rowsSelected
          .map((r) => {
            const row = documentsTableData[r.dataIndex];
            return row?.id ?? (Array.isArray(row) ? row[0] : undefined);
          })
          .filter(Boolean);
        setSelectedDocumentIds(ids);
      } else {
        setSelectedDocumentIds([]);
      }
    },
    customToolbarSelect: (selectedRows, displayData) => {
      documentsDisplayDataRef.current = displayData || [];
      const ids = (selectedRows?.data || []).map((d) => {
        const row = documentsTableData[d.dataIndex];
        return row?.id ?? (Array.isArray(row) ? row[0] : undefined);
      }).filter(Boolean);
      selectedDocumentIdsRef.current = ids;
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => {
              setPopUpModalContent({
                action: "bulkDelete",
                documentIds: selectedDocumentIdsRef.current?.length ? [...selectedDocumentIdsRef.current] : [],
              });
              openModal();
            }}
          >
            Delete ({selectedDocumentIdsRef.current?.length})
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => {
              setPopUpModalContent({
                action: "bulkMove",
                documentIds: selectedDocumentIdsRef.current?.length ? [...selectedDocumentIdsRef.current] : [],
              });
              openModal();
            }}
          >
            Move ({selectedDocumentIdsRef.current?.length})
          </Button>
        </Box>
      );
    },
    onRowClick: (rowData, rowMeta) => {
      const link = Array.isArray(rowData)
        ? rowData[4]
        : rowData?.s3FileUrl ?? documentsTableData[rowMeta.dataIndex]?.s3FileUrl;
      if (link) window.open(link);
    },
  };

  return (
    <Paper elevation={0}>
      <Box sx={{ display: "flex" }}>
        {/* LEFT: FOLDER TREE */}
        <Box
          sx={{
            width: "280px",
            borderRight: "1px solid #e0e0e0",
            p: 2,
            backgroundColor: "#fafafa",
            overflowY: "auto",
            maxHeight: "calc(100vh - 200px)",
          }}
        >
          {DOCUMENT_FOLDERS.map((folder) => {
            if (folder.children && folder.children.length > 0) {
              const parentCount = getFolderCount(folder);
              return (
                <Accordion
                  key={folder.key}
                  defaultExpanded={folder.children.some((c) => c.key === selectedFolderKey)}
                  disableGutters
                  sx={{
                    mb: 0.5,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#fff",
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      p: "8px 12px",
                      minHeight: "40px",
                      backgroundColor:
                        selectedFolderKey === folder.key ? "#e3f2fd" : "#fff",
                      borderBottom: "1px solid #f0f0f0",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                      <FolderOutlinedIcon sx={{ fontSize: 18, color: selectedFolderKey === folder.key ? "#1976d2" : "#333", flexShrink: 0 }} />
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color:
                            selectedFolderKey === folder.key ? "#1976d2" : "#333",
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {folder.label}
                      </Typography>
                      <Chip label={parentCount} size="small" sx={{ height: 20, fontSize: "0.75rem", fontWeight: 600, flexShrink: 0 }} />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {folder.children.map((child) => {
                      const childCount = folderDocumentCounts[child.key] || 0;
                      return (
                        <Box
                          key={child.key}
                          onClick={() => setSelectedFolderKey(child.key)}
                          sx={{
                            p: "8px 12px 8px 32px",
                            cursor: "pointer",
                            backgroundColor:
                              selectedFolderKey === child.key
                                ? "#e3f2fd"
                                : "transparent",
                            borderBottom: "1px solid #f5f5f5",
                            transition: "background-color 0.2s",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                            "&:last-child": {
                              borderBottom: "none",
                            },
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <FolderOutlinedIcon sx={{ fontSize: 16, color: selectedFolderKey === child.key ? "#1976d2" : "#666", flexShrink: 0 }} />
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight:
                                selectedFolderKey === child.key ? 600 : 400,
                              color:
                                selectedFolderKey === child.key
                                  ? "#1976d2"
                                  : "#666",
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {child.label}
                          </Typography>
                          <Chip label={childCount} size="small" sx={{ height: 18, fontSize: "0.7rem", fontWeight: 600, flexShrink: 0 }} />
                        </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            }

            const count = getFolderCount(folder);
            return (
              <Box
                key={folder.key}
                onClick={() => setSelectedFolderKey(folder.key)}
                sx={{
                  mb: 0.5,
                  p: "8px 12px",
                  cursor: "pointer",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  backgroundColor:
                    selectedFolderKey === folder.key ? "#e3f2fd" : "#fff",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <FolderOutlinedIcon sx={{ fontSize: 18, color: selectedFolderKey === folder.key ? "#1976d2" : "#333", flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontWeight:
                      selectedFolderKey === folder.key ? 600 : 400,
                    fontSize: "14px",
                    color:
                      selectedFolderKey === folder.key ? "#1976d2" : "#333",
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {folder.label}
                </Typography>
                <Chip label={count} size="small" sx={{ height: 20, fontSize: "0.75rem", fontWeight: 600, flexShrink: 0 }} />
              </Box>
            );
          })}
        </Box>

        {/* RIGHT: TABLE */}
        <Box sx={{ flex: 1 }}>
          <div className="d-flex justify-content-between align-items-center p-3" style={{ gap: "20px", flexWrap: "wrap" }}>
            <input
              type="text"
              placeholder="Search by filename..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "250px",
                padding: "6px 10px",
                border: "1px solid #ccc",
                borderRadius: "6px",
              }}
            />
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              {selectedDocumentIds.length > 0 && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      setPopUpModalContent({
                        action: "bulkDelete",
                        documentIds: selectedDocumentIds,
                      });
                      openModal();
                    }}
                  >
                    Delete Selected
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setPopUpModalContent({
                        action: "bulkMove",
                        documentIds: selectedDocumentIds,
                      });
                      openModal();
                    }}
                  >
                    Move Selected
                  </Button>
                </>
              )}

              {uploadDocumentAPI.status === "pending" ? (
                <LoadingButton
                  loading
                  startIcon={<FileUploadIcon />}
                  variant="contained"
                  color="secondary"
                >
                  Upload Document
                </LoadingButton>
              ) : (
                <Button
                  onClick={onUploadButtonClick}
                  startIcon={<FileUploadIcon />}
                  variant="contained"
                  color="secondary"
                >
                  Upload Document
                </Button>
              )}
            </div>

            <input
              type="file"
              ref={inputFileRef}
              onChangeCapture={onFileChange}
              hidden
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.docx"
            />
          </div>

          {/* PART 2: Rename Dialog UI */}
          {showRenamePrompt && selectedFiles.length > 0 && (
            <Box sx={{ mb: 3, p: 2, border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "#f9f9f9", mx: 3, mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Rename Documents Before Upload
              </Typography>

              {selectedFiles.map((file, index) => {
                const fileExtension = file.name.substring(file.name.lastIndexOf("."));
                const currentRenamedName = renamedFilesMap[index] || "";

                return (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box sx={{ fontSize: "0.85rem", color: "#666", mb: 0.5 }}>
                      Original: <strong>{file.name}</strong>
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      <input
                        type="text"
                        placeholder={`New name for file ${index + 1}`}
                        value={currentRenamedName}
                        onChange={(e) => {
                          const newMap = { ...renamedFilesMap };
                          newMap[index] = e.target.value;
                          setRenamedFilesMap(newMap);
                        }}
                        style={{
                          flex: 1,
                          padding: "8px 10px",
                          border: currentRenamedName.trim() === "" ? "1.5px solid #d32f2f" : "1px solid #ccc",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      />
                      <span style={{ fontSize: "14px", color: "#666", minWidth: "80px" }}>{fileExtension}</span>
                    </Box>
                    {currentRenamedName.trim() === "" && (
                      <Typography variant="caption" sx={{ color: "#d32f2f", display: "block", mt: 0.5 }}>
                        ⚠️ Filename required
                      </Typography>
                    )}
                  </Box>
                );
              })}

              {uploadError && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: "#ffebee", border: "1px solid #f5222d", borderRadius: "4px" }}>
                  <Typography variant="caption" sx={{ color: "#d32f2f", display: "block" }}>
                    ❌ {uploadError}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => uploadDocument(selectedFiles)}
                  startIcon={<FileUploadIcon />}
                  disabled={uploadDocumentAPI.status === "pending"}
                >
                  Upload
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setShowRenamePrompt(false);
                    setSelectedFiles([]);
                    setRenamedFilesMap({});
                    setUploadError("");
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

          {getDocumentsAPI.status === "pending" ? (
            <div className="text-center">
              <CircularProgress />
            </div>
          ) : documentsTableData.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No documents available in this folder.
              </Typography>
            </Box>
          ) : (
            <ThemeProvider theme={DATA_TABLE_THEME}>
              <MUIDataTable
                title={selectedFolderKey}
                data={documentsTableData}
                columns={documentsColumns}
                options={documentsTableOptions}
              />
            </ThemeProvider>
          )}
        </Box>
      </Box>

      <PopUpModal ref={popUpModalRef}>
        {popUpModalContent.action === "delete" ? (
          <DeleteDocumentModal documentId={popUpModalContent.documentId} />
        ) : popUpModalContent.action === "move" ? (
          <MoveDocumentModal
            documentId={popUpModalContent.documentId}
            currentFolderKey={popUpModalContent.currentFolderKey}
          />
        ) : popUpModalContent.action === "rename" ? (
          <RenameDocumentModal
            documentId={popUpModalContent.documentId}
            currentFileName={popUpModalContent.currentFileName}
          />
        ) : popUpModalContent.action === "bulkDelete" ? (
          <BulkDeleteDocumentsModal
            documentIds={popUpModalContent.documentIds || []}
          />
        ) : popUpModalContent.action === "bulkMove" ? (
          <BulkMoveDocumentsModal
            documentIds={popUpModalContent.documentIds || []}
          />
        ) : null}
      </PopUpModal>
    </Paper>
  );

  function ActionsCell({ documentId, link, currentFolderKey, currentFileName }) {
    const actions = {
      startItems: [],
      menuItems: [
        {
          label: "Rename",
          onClickHandler: () => {
            setPopUpModalContent({
              documentId,
              currentFileName: currentFileName ?? "",
              action: "rename",
            });
            openModal();
          },
        },
        {
          label: "Move to Folder",
          onClickHandler: () => {
            setPopUpModalContent({
              documentId,
              currentFolderKey: currentFolderKey ?? "PATIENT_UPLOADS",
              action: "move",
            });
            openModal();
          },
        },
        {
          label: "Delete",
          onClickHandler: () => {
            setPopUpModalContent({
              documentId,
              action: "delete",
            });
            openModal();
          },
        },
      ],
    };
    return <TableRowActionsCell actions={actions} align="right" />;
  }

  function DeleteDocumentModal({ documentId }) {
    return (
      <Box
        className="px-3 py-4"
        sx={{
          maxWidth: "350px",
        }}
      >
        <Typography id="modal-modal-title" variant="h3" color="text">
          Do you want to confirm deleting the document?
        </Typography>
        <Stack direction="row" spacing={3} className="mt-4">
          <Button
            onClick={() => {
              deleteDocument(documentId);
              closeModal();
            }}
            variant="contained"
          >
            Confirm
          </Button>
          <Button onClick={() => closeModal()} variant="outlined">
            Cancel
          </Button>
        </Stack>
      </Box>
    );
  }

  function RenameDocumentModal({ documentId, currentFileName }) {
    const [newFileName, setNewFileName] = useState(currentFileName ?? "");
    useEffect(() => {
      setNewFileName(currentFileName ?? "");
    }, [documentId, currentFileName]);

    function handleRename() {
      const trimmed = newFileName.trim();
      if (!trimmed) return;
      renameDocumentAPI.execute(props.hospitalId, props.patientId, documentId, trimmed);
      closeModal();
    }

    return (
      <Box className="px-3 py-4" sx={{ minWidth: "400px" }}>
        <Typography variant="h3" color="text" sx={{ mb: 2 }}>
          Rename Document
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the new name for the document (include file extension, e.g. .pdf).
        </Typography>
        <TextField
          fullWidth
          label="Document name"
          value={newFileName}
          onChange={(e) => setNewFileName(e.target.value)}
          placeholder="e.g. Report 2024.pdf"
          sx={{ mb: 3 }}
          onKeyDown={(e) => e.key === "Enter" && handleRename()}
        />
        <Stack direction="row" spacing={3}>
          <LoadingButton
            onClick={handleRename}
            variant="contained"
            disabled={!newFileName.trim()}
            loading={renameDocumentAPI.status === "pending"}
          >
            Rename
          </LoadingButton>
          <Button onClick={() => closeModal()} variant="outlined">
            Cancel
          </Button>
        </Stack>
      </Box>
    );
  }

  function MoveDocumentModal({ documentId, currentFolderKey }) {
    const [selectedNewFolder, setSelectedNewFolder] = useState("");
    const validFolders = getValidUploadFolders();
    const displayCurrentFolder = currentFolderKey === "PATIENT_UPLOADS" ? "Patient Uploads" : getSelectedFolderLabel(currentFolderKey) || currentFolderKey;

    function handleMoveDocument() {
      if (!selectedNewFolder) return;
      moveDocumentAPI.execute(props.hospitalId, props.patientId, documentId, selectedNewFolder);
      closeModal();
    }

    return (
      <Box className="px-3 py-4" sx={{ minWidth: "400px" }}>
        <Typography variant="h3" color="text" sx={{ mb: 2 }}>
          Move Document to Folder
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Current folder: <strong>{displayCurrentFolder}</strong>
        </Typography>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select New Folder</InputLabel>
          <Select
            value={selectedNewFolder}
            onChange={(e) => setSelectedNewFolder(e.target.value)}
            label="Select New Folder"
          >
            {validFolders.map((folder) => (
              <MenuItem
                key={folder.key}
                value={folder.key}
                disabled={folder.key === currentFolderKey}
              >
                {folder.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={3}>
          <LoadingButton
            onClick={handleMoveDocument}
            variant="contained"
            disabled={!selectedNewFolder || selectedNewFolder === currentFolderKey}
            loading={moveDocumentAPI.status === "pending"}
          >
            Move
          </LoadingButton>
          <Button onClick={() => closeModal()} variant="outlined">
            Cancel
          </Button>
        </Stack>
      </Box>
    );
  }

  function BulkDeleteDocumentsModal({ documentIds }) {
    const count = Array.isArray(documentIds) ? documentIds.length : 0;
    if (count === 0) return null;

    return (
      <Box
        className="px-3 py-4"
        sx={{
          maxWidth: "380px",
        }}
      >
        <Typography id="modal-modal-title" variant="h3" color="text">
          Do you want to delete {count} document{count > 1 ? "s" : ""}?
        </Typography>
        <Stack direction="row" spacing={3} className="mt-4">
          <Button
            onClick={() => {
              deleteDocuments(documentIds);
              closeModal();
            }}
            variant="contained"
            color="error"
          >
            Confirm
          </Button>
          <Button onClick={() => closeModal()} variant="outlined">
            Cancel
          </Button>
        </Stack>
      </Box>
    );
  }

  function BulkMoveDocumentsModal({ documentIds }) {
    const [selectedNewFolder, setSelectedNewFolder] = useState("");
    const validFolders = getValidUploadFolders();
    const count = Array.isArray(documentIds) ? documentIds.length : 0;

    if (count === 0) return null;

    function handleMoveSelectedDocuments() {
      if (!selectedNewFolder || count === 0) return;
      moveDocuments(documentIds, selectedNewFolder);
      closeModal();
    }

    return (
      <Box className="px-3 py-4" sx={{ minWidth: "400px" }}>
        <Typography variant="h3" color="text" sx={{ mb: 2 }}>
          Move {count} document{count > 1 ? "s" : ""} to folder
        </Typography>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select New Folder</InputLabel>
          <Select
            value={selectedNewFolder}
            onChange={(e) => setSelectedNewFolder(e.target.value)}
            label="Select New Folder"
          >
            {validFolders.map((folder) => (
              <MenuItem key={folder.key} value={folder.key}>
                {folder.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={3}>
          <LoadingButton
            onClick={handleMoveSelectedDocuments}
            variant="contained"
            disabled={!selectedNewFolder}
            loading={moveDocumentAPI.status === "pending"}
          >
            Move
          </LoadingButton>
          <Button onClick={() => closeModal()} variant="outlined">
            Cancel
          </Button>
        </Stack>
      </Box>
    );
  }

}

function ParentCard({
  index,
  hospitalId,
  patientId,
  isAddingParent,
  parentData,
  refreshParentsListHandler,
}) {
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
  } = useForm({ criteriaMode: "all", mode: "onTouched" });

  useEffect(() => {
    if (!isAddingParent) {
      reset(parentData);
    }
  }, [isAddingParent]);

  const addPatientParentAPI = useAPI(
    PatientService.addPatientParent,
    () => {
      refreshParentsListHandler();
    },
    true
  );

  const updatePatientParentAPI = useAPI(
    PatientService.updatePatientParent,
    () => { },
    true
  );

  const removePatientParentAPI = useAPI(
    PatientService.removePatientParent,
    () => {
      refreshParentsListHandler();
    }
  );

  function addPatientParent(data) {
    addPatientParentAPI.execute(hospitalId, patientId, data);
  }

  function updatePatientParent(data) {
    updatePatientParentAPI.execute(hospitalId, patientId, parentData.id, data);
  }

  function removePatientParent() {
    if (isAddingParent) {
      refreshParentsListHandler();
      return;
    }
    removePatientParentAPI.execute(hospitalId, patientId, parentData.id);
  }

  return (
    <Card variant="outlined" className="mb-3">
      <form
        onSubmit={handleSubmit(
          isAddingParent ? addPatientParent : updatePatientParent
        )}
      >
        <CardContent>
          <div className="d-flex flex-wrap justify-content-between">
            <div className="mb-3">
              <label
                htmlFor={`parentFirstNameInputs-${index}`}
                className="form-label"
              >
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                className="form-control"
                id={`parentFirstNameInputs-${index}`}
                {...register("firstName", {
                  required: "First Name is required",
                  pattern: {
                    value: RegexUtils.NAME,
                    message: "Fist Name is invalid",
                  },
                })}
              />
              <InputErrorMessage name="firstName" errors={errors} />
            </div>
            <div className="mb-3">
              <label
                htmlFor={`parentLastNameInput-${index}`}
                className="form-label"
              >
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                className="form-control"
                id={`parentLastNameInput-${index}`}
                {...register("lastName", {
                  required: "Last Name is required",
                  pattern: {
                    value: RegexUtils.NAME,
                    message: "Last Name is invalid",
                  },
                })}
              />
              <InputErrorMessage name="lastName" errors={errors} />
            </div>
            <div className="mb-3">
              <label
                htmlFor={`parentEmailInput-${index}`}
                className="form-label"
              >
                Email ID
              </label>
              <input
                type="email"
                name="email"
                className="form-control"
                id={`parentEmailInput-${index}`}
                {...register("email", {
                  required: "Email ID is required",
                  pattern: {
                    value: RegexUtils.EMAIL,
                    message: "Email ID is invalid",
                  },
                })}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="phoneInput" className="form-label">
                Phone
              </label>
              <input
                type="text"
                name="phone"
                className="form-control"
                id="phoneInput"
                {...register("phone", {
                  required: "Phone is required",
                  pattern: {
                    value: RegexUtils.PHONE,
                    message: "Phone is invalid",
                  },
                })}
                onKeyUp={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, "");
                  setValue("phone", digitsOnly);
                }}
              />
              <InputErrorMessage name="phone" errors={errors} />
            </div>
            <div className="mb-3">
              <label htmlFor="countryCode" className="form-label">
                Country Code
              </label>
              <select
                className="form-select"
                id="countryCodeInput"
                aria-label="Default select example"
                {...register("countryCode", {
                  required: "Country Code is required",
                  minLength: {
                    value: 2,
                    message: "Country Code must exceed 2 characters",
                  },
                  maxLength: {
                    value: 5,
                    message: "Country Code must not exceed 5 characters",
                  },
                })}
              >
                <option></option>
                {COUNTRY_CODES_LIST.map((item, index) => (
                  <option key={item.value + index} value={item.value}>
                    {item.country}
                  </option>
                ))}
              </select>
              <InputErrorMessage name="countryCode" errors={errors} />
            </div>
            <div className="mb-3">
              <label className="form-label d-block">Gender</label>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="gender"
                  id={`parentGenderInputMale-${index}`}
                  value="Male"
                  {...register("gender", {
                    required: "Gender is required",
                  })}
                />
                <label
                  className="form-check-label"
                  htmlFor={`parentGenderInputMale-${index}`}
                >
                  Male
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="gender"
                  id={`parentGenderInputFemale-${index}`}
                  value="Female"
                  {...register("gender", {
                    required: "Gender is required",
                  })}
                />
                <label
                  className="form-check-label"
                  htmlFor={`parentGenderInputFemale-${index}`}
                >
                  Female
                </label>
              </div>
              <InputErrorMessage name="gender" errors={errors} />
            </div>
            <div>
              <label
                htmlFor={`parentMaritalStatusInput-${index}`}
                className="form-label"
              >
                Marital Status
              </label>
              <select
                className="form-select"
                name="maritalStatus"
                id={`parentMaritalStatusInput-${index}`}
                aria-label="Default select example"
                {...register("maritalStatus", {
                  required: "Marital Status is required",
                })}
              >
                <option></option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="NA">NA</option>
              </select>
              <InputErrorMessage name="maritalStatus" errors={errors} />
            </div>
          </div>
        </CardContent>
        <CardActions>
          <Button size="small" type="submit">
            {isAddingParent ? "Add" : "Save"}
          </Button>
          <Button
            size="small"
            color="error"
            onClick={() => removePatientParent()}
          >
            Remove
          </Button>
        </CardActions>
      </form>
    </Card>
  );
}

export default PatientProfile;